import React, { useContext, useMemo, useEffect } from "react"
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs"
import { Platform, Pressable } from "react-native"
import * as Notifications from 'expo-notifications'
// import * as Location from 'expo-location'
import { ThemeContext } from "../../utility/context/ThemeContext"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { Ionicons } from "@expo/vector-icons"
import Haptics from "../../utility/haptics"
import Footer from "../../utility/footer"
import DashboardStack from "./DashboardStack"
import TransactionsStack from "./TransactionsStack"
import ExploreCompaniesStack from "./ExploreCompaniesStack"
import SettingsStack from "./SettingsStack"
import { useAppSelector } from "../../store"
import { usePermissions } from "../../utility/context/PermissionsContext"
import { Log } from "../../utility"

const Tabs = createBottomTabNavigator()

const MainAppTabs = () => {
  const inWeb = Platform.OS === 'web'
  const theme = useContext(ThemeContext).theme
  const insets = useSafeAreaInsets()

  const { institutions } = useAppSelector(state => state.accountsSlice)

  const institutionsWithErrors = useMemo(() => {
    return institutions.filter(institution => institution.error)
  }, [institutions])

  const { enablePermission, disablePermission } = usePermissions()

  useEffect(() => {
    let isMounted = true
    if (Platform.OS === 'web') return

    const maybePromptPermissions = async () => {
      try {
        // const [{ status: locationStatus }, { status: notifStatus }] = await Promise.all([
        //   Location.getForegroundPermissionsAsync(),
        //   Notifications.getPermissionsAsync(),
        // ])

        // Prompt for location only if never asked on the device and we haven't asked in-app yet
        // if (locationStatus === 'undetermined') {
        //   if (!isMounted) return
        //   // request location permission
        //   const { status } = await Location.requestForegroundPermissionsAsync()

        //   if (status === 'granted') {
        //     enablePermission('location')
        //   }

        //   else {
        //     disablePermission('location')
        //   }
        // }
        
        const { status: notifStatus } = await Notifications.getPermissionsAsync()

        // Prompt for notifications only if never asked on the device
        if (notifStatus === 'undetermined') {
          if (!isMounted) return
          // request notifications permission
          const { status } = await Notifications.requestPermissionsAsync()

          if (status === 'granted') {
            enablePermission('notifications')
          }

          else {
            disablePermission('notifications')
          }
        }
      } catch (error) {
        Log.error('MainAppTabs/maybePromptPermissions: Error prompting permissions', { error })
      }
    }

    maybePromptPermissions()
    return () => { isMounted = false }
  }, [enablePermission, disablePermission])

  return <>
    <Tabs.Navigator
      id={undefined}
      screenOptions={
        {
          lazy: false,
          headerShown: false,
          ...(inWeb ? {
            tabBarStyle: { display: "none" },
          } : {
            paddingBottom: insets.bottom,
            tabBarActiveTintColor: theme.colors.tabActiveText,
            tabBarInactiveTintColor: theme.colors.tabInactiveText,
            tabBarHideOnKeyboard: true,
            tabBarStyle: {
              paddingBottom: insets.bottom,
              borderWidth: 0,
              borderTopWidth: 0,
              backgroundColor: theme.colors.tabBackground,
            },
            tabBarLabelStyle: {
              fontSize: 12,
              fontFamily: "poppins-regular"
            },
            tabBarButton: function TabBarButton(props: any) {
              return (
                <Pressable
                  {...props}
                  onPress={() => {
                    Haptics.selection()
                    props.onPress()
                  }}
                />
              )
            }
          })
        }
      }
    >
      <Tabs.Screen
        name='DashboardStack'
        component={DashboardStack}
        options={{
          tabBarLabel: "Home",
          tabBarIcon: inWeb ? undefined : ({focused, color}) => (
            <Ionicons name={focused ? 'home' : 'home-outline'} size={20} color={color}/>
          ),
          title: 'Dashboard'
        }}
      />
      <Tabs.Screen
        name='TransactionsStack'
        component={TransactionsStack}
        options={{
          tabBarLabel: "Transactions",
          tabBarIcon: inWeb ? undefined : ({focused, color}) => (
            <Ionicons name={focused ? 'receipt' : 'receipt-outline'} size={20} color={color}/>
          ),
          title: "Transactions",
        }}
      />
      <Tabs.Screen
        name='ExploreCompaniesStack'
        component={ExploreCompaniesStack}
        options={{
          tabBarLabel: "Explore",
          tabBarIcon: inWeb ? undefined : ({focused, color}) => (
            <Ionicons name={focused ? 'storefront' : 'storefront-outline'} size={20} color={color}/>
          ),
          title: "Explore Companies",
        }}
      />
      <Tabs.Screen
        name='Settings'
        component={SettingsStack}
        options={{
          headerShown: false,
          tabBarLabel: "Settings",
          tabBarIcon: inWeb ? undefined : ({focused, color}) => (
            <Ionicons name={focused ? 'settings' : 'settings-outline'} size={20} color={color}/>
          ),
          // show badge if there are any institutions with errors
          tabBarBadge: institutionsWithErrors.length || undefined,
          tabBarBadgeStyle: {
            backgroundColor: theme.colors.danger,
            color: theme.colors.white,
            fontFamily: "poppins-regular",
          }
        }}
      />
    </Tabs.Navigator>
    {
      inWeb ? <Footer/> : null
    }
  </>
}

export default MainAppTabs


